import { Injectable } from '@angular/core';
import { CorporateAccount, Employee, EmployeePayload, OnboardBusinessPayload } from '../../models/corporate-account';
import { ResourceService } from './resource';
import { HttpClient } from '@angular/common/http';
import { CacheService } from '../cache';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CorporateAccountService extends ResourceService<CorporateAccount> {
  constructor(protected http: HttpClient, protected cache: CacheService) {
    super(http, cache, 'businesses');
  }

  // Summary

  /**
   * Sends a GET request to the /businesses endpoint
   * @remarks
   * to get all businesses for admin
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getAllBusinesses(params?: any) {
    const queryParams: any = { ...params };
    if (queryParams?.q !== undefined && queryParams?.q !== null) {
      const q = String(queryParams.q).trim();
      if (q.includes('@')) {
        queryParams.email = q;
      } else if (q.length > 0) {
        queryParams.name = q;
      }
      delete queryParams.q;
    }
    delete queryParams?.search;

    return this.http.get<any>(`${this.endpoint}`, { params: queryParams }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  override search(queryOptions?: any): Observable<any> {
    return this.getAllBusinesses(queryOptions).pipe(
      map((res: any) => {
        const rawData = res?.businesses?.data?.businesses
          || res?.data?.businesses
          || res?.businesses?.data
          || res?.data
          || res?.businesses
          || (Array.isArray(res) ? res : []);
        const count = res?.count ?? res?.total ?? res?.businesses?.count ?? rawData.length;
        const page = res?.page ?? queryOptions?.page ?? 1;
        const page_size = res?.page_size ?? queryOptions?.size ?? 10;

        return {
          page,
          count,
          page_size,
          data: rawData
        };
      })
    );
  }




  /**
   * Sends a GET request to the /businesses/:id/employees endpoint
   * @remarks
   * to summary of a business
   * @param {string} id Id Business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getBusinessSummary(id: string) {
    return this.http.get<any>(`${this.endpoint}/${id}/summary`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  // Employees

  /**
   * Sends a GET request to the /businesses/:id/employees endpoint
   * @remarks
   * to get all employees of a business
   * @param {string} id Id Business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getEmployeesOfABusiness(params: any) {
    const id = params.id;
    delete params.id;
    return this.http
      .get<any>(`${this.endpoint}/${id}/employees`, { params })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /businesses/employees/:id endpoint
   * @remarks
   * to get details of an employee
   * @param {string} id Id of employee
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getEmployeeOfABusinessById(id: string) {
    return this.http.get<any>(`${this.endpoint}/employees/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /businesses/:id/employees/invite endpoint
   * @remarks
   * to invite employees
   * @param {string} id Id of business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  inviteEmployee(id: string, payload: any) {
    return this.http
      .post<any>(`${this.endpoint}/${id}/employees/invite`, payload)
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }
  /**
   * Sends a POST request to the /businesses/:id/employees/invite endpoint
   * @remarks
   * to invite employees
   * @param {string} id Id of business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  resendInvite(id: string, payload: any) {
    return this.http
      .post<any>(`${this.endpoint}/${id}/employees/resend-invite`, payload)
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a PUT request to the /businesses/employees/:id/ endpoint
   * @remarks
   * to update status of an employee
   * @param {string} id Id of the employee
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  updateEmployeeStatus(id: string, payload: any) {
    return this.http.put<any>(`${this.endpoint}/employees/${id}`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a PUT request to the /businesses/employees/:id/ endpoint
   * @remarks
   * to update status of an employee
   * @param {string} id Id of the employee
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  updateInvitedEmployeeProfile(id: string, payload: any) {
    return this.http.put<any>(`${this.endpoint}/employees/${id}`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /businesses/admin/employees endpoint
   * @remarks
   * to create an admin employee
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  createAdminEmployee(payload: EmployeePayload) {
    return this.http.post<any>(`${this.endpoint}/admin/employees`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /businesses/admin/onboard-business endpoint
   * @remarks
   * to admin onboarding business
   * @param {OnboardBusinessPayload} payload The payload containing the business and employee details
   * @returns {Observable} Returns an observable of any, with a server-side representation of the matching resources
   */
  onboardBusiness(payload: OnboardBusinessPayload) {
    return this.http.post<any>(`${this.endpoint}/admin/onboard-business`, payload);
  }

  /**
   * Sends a PUT request to the /businesses/:id/status endpoint
   * @remarks
   * to update status of a business
   * @param {string} id Id of the business
   * @returns {Observable} Returns an observable of any
   */
  updateBusinessStatus(id: string, payload: { status: 'active' | 'suspended' | 'blocked' | 'rejected', reason?: string }) {
    return this.http.put<any>(`${this.endpoint}/${id}/status`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  // Trips
  /**
   * Sends a GET request to the /businesses/:id/trips endpoint
   * @remarks
   * to get all trips of an employee
   * @param {string} id Id of employee
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getEmployeeTrips(id: string, params?: any) {
    return this.http
      .get<any>(`${this.endpoint}/employees/${id}/trips`, { params })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /businesses/:id/trips endpoint
   * @remarks
   * to get all trips of an employee
   * @param {string} id Id of the business you want to get its trip history
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getBusinessTrips(id: string, params?: any) {
    return this.http.get<any>(`${this.endpoint}/${id}/trips`, { params }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /businesses/:id/trips/export endpoint
   * @remarks
   * to export all trips of an employee
   * @param {string} id Id of the business you want to get its trip history
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  exportBusinessTrips(id: string, params?: any) {
    return this.http.get<any>(`${this.endpoint}/${id}/trips/export`, { params }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /businesses/:id/trips/search endpoint
   * @remarks
   * to get all trips of an employee
   * @param {string} id Id of the business you want to get its trip history
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  searchBusinessTrips(id: string, params?: any) {
    return this.http
      .get<any>(`${this.endpoint}/${id}/trips/search`, { params })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a PUT request to the /businesses/:id/trips endpoint
   * @remarks
   * to update trip of a business
   * @param {string} id Id of the business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  updateEmployeeTrip(id: string, payload: any) {
    return this.http
      .put<any>(`${this.endpoint}/employees/${id}/trips`, payload)
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /businesses/trips endpoint
   * @remarks
   * to get all trips of a company
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getAllBusinessTrips() {
    return this.http.get<any>(`${this.endpoint}/trips`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /businesses/trips/:id endpoint
   * @remarks
   * to get details of a trip
   * @param {string} id Id of the trip
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getBusinessTripsById(id: string) {
    return this.http.get<any>(`${this.endpoint}/trips/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  // Transactions

  /**
   * Sends a GET request to the /businesses/:id/transactions endpoint
   * @remarks
   * to get transactions of a business
   * @param {string} id Id of the Business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getBusinessTransactions(id: string, params: any) {
    return this.http
      .get<any>(`${this.endpoint}/${id}/transactions`, { params })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }
  /**
   * Sends a GET request to the /businesses/:id/transactions/export endpoint
   * @remarks
   * to export transactions of a business
   * @param {string} id Id of the Business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  exportBusinessTransactions(id: string, params: any) {
    return this.http
      .get<any>(`${this.endpoint}/${id}/transactions/export`, { params })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /businesses/transactions/:id endpoint
   * @remarks
   * to get details of a transactions
   * @param {string} id Id of the transaction
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getBusinessTransactionsByid(id: string) {
    return this.http.get<any>(`${this.endpoint}/transactions/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /businesses/employee/:id/transactions endpoint
   * @remarks
   * to get transactions of an employee
   * @param {string} id Id of the transaction
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getTransactionsOfEmployee(id: string) {
    return this.http
      .get<any>(`${this.endpoint}/employees/${id}/transactions`)
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
* Sends a GET request to the /businesses/:id/transactions/download
 endpoint
* @remarks
* Download receipt of a transaction
* @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
*/
  downloadTransactionReceipts(id: string, params: any) {
    return this.http
      .get<any>(`${this.endpoint}/${id}/transactions/download`, { params })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  /**
   * Sends a GET request to the /businesses/:id/invoices/download endpoint
   * @remarks
   * Download receipt/invoice of a business
   * @param {string} id Business ID (:id)
   * @param {any} params Query parameters (e.g. { q: invoiceId } or invoiceId as string)
   * @returns {Observable} Returns an observable of any
   */
  downloadInvoiceReceipt(id: string, params: any) {
    const queryParams = typeof params === 'string' ? { q: params } : params;
    return this.http
      .get<any>(`${this.endpoint}/${id}/invoices/download`, { params: queryParams })
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  downloadInvoice(id: string, params: any) {
    return this.downloadInvoiceReceipt(id, params);
  }
  /**
   * Sends a GET request to the /businesses/transactions endpoint
   * @remarks
   * to get all transactions
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getAllTransactions() {
    return this.http.get<any>(`${this.endpoint}/transactions`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  inviteEmployees(id: string, body: Employee[]) {
    return this.http
      .post<any>(`${this.endpoint}/${id}/employees/invite`, body)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  setupPreference(id: string, body: any) {
    return this.http
      .post<any>(`${this.endpoint}/${id}/preference`, body)
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  getPreference(id: string) {
    return this.http.get<any>(`${this.endpoint}/${id}/preference`).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }



  /**
   * Sends a POST request to the /request endpoint
   * @remarks
   * to send request for a demo
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  sendDemoRequest(payload: any) {
    return this.http.post<any>(`request/save`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /request endpoint
   * @remarks
   * to send request for a demo
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getDemoRequest(payload: any) {
    return this.http.get<any>(`request`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  //  ================== Extra services ===================

  /**
   * Sends a POST request to the /addon/flight endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createExtraServiceFlight(payload: any) {
    return this.http.post<any>(`addon/flight`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/flight endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceFlight(payload?: any) {
    return this.http.get<any>(`addon/flight`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /addon/car-rental endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createExtraServiceCarRentals(payload: any) {
    return this.http.post<any>(`addon/car-rental`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/car-rental endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceCarRentals(payload?: any) {
    return this.http.get<any>(`addon/car-rental`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /addon/airport-transfer endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createExtraServiceAirportTransfer(payload: any) {
    return this.http.post<any>(`addon/airport-transfer`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/airport-transfer endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceAirportTransfer(payload?: any) {
    return this.http.get<any>(`addon/airport-transfer`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /addon/airport-lounge endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createExtraServiceAirportLounge(payload: any) {
    return this.http.post<any>(`addon/airport-lounge`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/airport-lounge endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceAirportLounge(payload?: any) {
    return this.http.get<any>(`addon/airport-lounge`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /addon/immigration-and-visa endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createExtraServiceVisa(payload: any) {
    return this.http.post<any>(`addon/immigration-and-visa`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/immigration-and-visa endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceVisa(payload?: any) {
    return this.http.get<any>(`addon/immigration-and-visa`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /addon/removal endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createExtraServiceRemoval(payload: any) {
    return this.http.post<any>(`addon/removal`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/removal endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceRemoval(payload?: any) {
    return this.http.get<any>(`addon/removal`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
 * Sends a POST request to the /addon/longterm-rental endpoint
 * @remarks
 * to send a request for flight service
 * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
 */
  createExtraServiceRental(payload: any) {
    return this.http.post<any>(`addon/longterm-rental`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/longterm-rental endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceRental(payload?: any) {
    return this.http.get<any>(`addon/longterm-rental`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
 * Sends a POST request to the /addon/train-ferry endpoint
 * @remarks
 * to send a request for flight service
 * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
 */
  createExtraServiceTrainFerry(payload: any) {
    return this.http.post<any>(`addon/train-ferry`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }
  /**
   * Sends a GET request to the /addon/train-ferry endpoint
   * @remarks
   * to send a request for flight service
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getExtraServiceTrainFerry(payload?: any) {
    return this.http.get<any>(`addon/train-ferry`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  // Approvals
  // approvalsEndpoint: string = 'https://clooper-api.ap76f32dc77r0.eu-west-2.cs.amazonlightsail.com/api/v2';
  /**
   * Sends a GET request to the /approvals endpoint
   * @remarks
   * to get all approvals
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getAllApprovals(payload?: any) {
    return this.http.get<any>(`approvals`, { params: payload }).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /approvals/:id/ endpoint
   * @remarks
   * to get the approval that matches the provided id
   * @param {string} id Id of the approval you want to get
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getApproval(id: string) {
    return this.http.get<any>(`approvals/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /approvals endpoint
   * @remarks
   * to send a request for approvals you to create
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  createApproval(payload: any) {
    return this.http.post<any>(`approvals`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a PUT request to the /approvals/:id endpoint
   * @remarks
   * to update approval that matches the provided id
   * @param {string} id Id of the approval you want to update
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  updateApproval(id: string, payload: any) {
    return this.http.put<any>(`approvals/${id}`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a DELETE request to the approvals/:id endpoint
   * @remark
   * To delete an approval request
   *@param {string} id Id of the approval you want to delete
   */
  deleteApproval(id: string) {
    return this.http.delete(`approvals/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}/approvals`);
        this.cache.remove(`${this.endpoint}/approvals`);
        return resp;
      })
    );
  }

  /**
   * Sends a PUT request to the /approvals/:id/accept endpoint
   * @remarks
   * to update an approval to accept the request
   * @param {string} id Id of the approval you want to accept
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  acceptApproval(id: string, payload: any) {
    return this.http
      .put<any>(`approvals/${id}/accept`, payload)
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }
  /**
   * Sends a PUT request to the /approvals/:id/reject endpoint
   * @remarks
   * to update an approval to reject the request
   * @param {string} id Id of the approval you want to reject
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  rejectApproval(id: string, payload: any) {
    return this.http
      .put<any>(`approvals/${id}/reject`, payload)
      .pipe(
        map((resp: any) => {
          this.cache.remove(`${this.endpoint}`);
          this.cache.remove(`${this.endpoint}`);
          return resp;
        })
      );
  }

  // Booking Limits
  /**
   * Sends a GET request to the /limit endpoint
   * @remarks
   * @param {string} id Id of the business of booking limit you want to get
   * to get all booking limits
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getAllBookingLimits(id: string) {
    return this.http.get<any>(`limit?business=${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /limit/:id/ endpoint
   * @remarks
   * to get the booking limit that matches the provided id
   * @param {string} id Id of the booking limit you want to get
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  getBookingLimit(id: string) {
    return this.http.get<any>(`limit/:id?business=${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a POST request to the /limit endpoint
   * @remarks
   * to create a new booking limit
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  createBookingLimit(payload: any) {
    return this.http.post<any>(`limit`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a PUT request to the /limit/:id endpoint
   * @remarks
   * to update the booking limit that matches the provided id
   * @param {string} id Id of the booking limit you want to update
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  updateBookingLimit(id: string, payload: any) {
    return this.http.put<any>(`limit/${id}`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a DELETE request to the /limit/:id endpoint
   * @remarks
   * to delete the booking limit that matches the provided id
   * @param {string} id Id of the booking limit you want to delete
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  deleteBookingLimit(id: string) {
    return this.http.delete(`/limit/${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a GET request to the /approvals/employee/pay endpoint
   * @remarks
   * to get the configuration of a company by allowing employee to pay or not.
   * @param {string} id Id of the business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */

  getCanEmployeePay(id: string) {
    return this.http.get(`approvals/employee/pay?business=${id}`).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }

  /**
   * Sends a PUT request to the /approvals/employee/pay endpoint
   * @remarks
   * to update the configuration of a company by allowing employee to pay or not.
   * @param {string} id Id of the business
   * @returns {Observable} Returns an observable of any, with a server-side representations of the matching resources
   */
  toggleCanEmployeePay(payload: any, id: string) {
    return this.http.put(`approvals/employee/pay/${id}?business=${id}`, payload).pipe(
      map((resp: any) => {
        this.cache.remove(`${this.endpoint}`);
        this.cache.remove(`${this.endpoint}`);
        return resp;
      })
    );
  }


}
